"use client";

import { useEffect, useState } from "react";
import { Link } from "@frontend/lib/link";
import {
  acceptOfferAction,
  approveDraftAction,
  cancelCollabAction,
  confirmLiveAction,
  confirmPaymentAction,
  rejectPaymentAction,
  requestRevisionAction,
  submitDraftAction,
  submitLiveUrlAction,
} from "@frontend/lib/actions";
import { ActionForm, SubmitButton } from "@frontend/components/action-form";
import { CountdownPill } from "@frontend/components/countdown";
import { CounterOfferDialog } from "@frontend/components/collab/counter-offer-dialog";
import { PayNowDialog, type PayMethodView } from "@frontend/components/collab/pay-now-dialog";
import { ProofRow } from "@frontend/components/collab/payment-proof";
import {
  Callout,
  Disclosure,
  Field,
  LinkButton,
  Money,
  OptionalField,
  cx,
} from "@frontend/components/ui";
import { payMethodsFor } from "@frontend/lib/collab-pay-methods";
import type { ActiveTimer, CollabRoom } from "@creators/backend/lib/collabs/queries";
import { formatINR } from "@creators/backend/lib/money";
import {
  effectiveCollabStatus,
  type CollabStatus,
} from "@creators/backend/lib/collabs/state-machine";

type Proof = CollabRoom["proofs"][number];

type Props = {
  room: CollabRoom;
  viewer: "creator" | "brand" | "admin";
  verified: boolean;
  canAct: boolean;
  /** The deadline currently running on this status, if any. */
  timer: ActiveTimer;
  className?: string;
};

/**
 * One card at the top of the sidebar for whatever needs doing right now —
 * accept, pay, confirm receipt, submit work, review a draft, and so on.
 * Status panels below stay read-only.
 */
export function CollabActionBox({ room, viewer, verified, canAct, timer, className }: Props) {
  const rawStatus = room.collab.status as CollabStatus;
  const status = effectiveCollabStatus(rawStatus, room.milestones);
  const offer = room.currentOffer;

  const isCreator = viewer === "creator";
  const dueMilestone = room.milestones.find((m) => m.status === "due") ?? null;
  const paidMilestone = room.milestones.find((m) => m.status === "paid") ?? null;
  // Confirming receipt is a judgement about evidence, so the evidence travels
  // with the action instead of sitting in the payments panel further down.
  const paidProof = paidMilestone
    ? (room.proofs
        .filter((p) => p.milestoneId === paidMilestone.id && p.status === "pending")
        .at(-1) ?? null)
    : null;
  const paymentDue = Boolean(dueMilestone);
  const paymentOutstanding = room.milestones.some((m) => m.status === "due" || m.status === "paid");
  // Admins observe. A finished collab has nothing left to do unless money is still
  // moving — live_period reads as completed in the UI, but tranches can still be due.
  const actionable =
    viewer !== "admin" &&
    (status !== "completed" || paymentDue || paymentOutstanding || Boolean(paidMilestone));
  const needsPayMethods =
    Boolean(offer) &&
    actionable &&
    !isCreator &&
    Boolean(dueMilestone) &&
    Boolean(room.payoutDetails);

  const [payMethods, setPayMethods] = useState<PayMethodView[]>([]);
  const dueAmount = dueMilestone?.amount ?? null;
  const payoutKey = room.payoutDetails
    ? [
        room.payoutDetails.defaultMethod,
        room.payoutDetails.upiId,
        room.payoutDetails.bankAccountNumber,
        room.payoutDetails.bankIfsc,
        room.payoutDetails.otherPayoutDetails,
      ].join("|")
    : "";

  useEffect(() => {
    if (!needsPayMethods || dueAmount == null) {
      setPayMethods([]);
      return;
    }
    let cancelled = false;
    void payMethodsFor(room, dueAmount).then((methods) => {
      if (!cancelled) setPayMethods(methods);
    });
    return () => {
      cancelled = true;
    };
    // room is read for payout/creator fields; payoutKey + dueAmount gate reloads.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- avoid regenerating QR on every sync
  }, [needsPayMethods, dueAmount, payoutKey, room.collab.id, room.collab.reference, room.creator.displayName]);

  if (!offer) return null;

  const canAccept =
    canAct &&
    (status === "offer_pending" || status === "negotiating") &&
    offer.createdBy !== viewer;
  const needsPayoutDetails =
    isCreator && offer.compensationType !== "barter" && !room.payoutDetails;

  const content = actionable
    ? resolveActionContent({
        room,
        status,
        isCreator,
        verified,
        canAct,
        canAccept,
        needsPayoutDetails,
        paymentDue,
        paymentOutstanding,
        dueMilestone,
        paidMilestone,
        paidProof,
        payMethods,
      })
    : null;

  if (!content && !timer) return null;

  const deadline = timer ? new Date(timer.deadline).toISOString() : null;
  // With no action to take, the deadline is the whole point of the card.
  const title = content?.title ?? "Deadline";

  return (
    // Black spotlight — the one card that needs acting on. Tokens are remapped
    // via `.action-spotlight` so children stay readable in light and dark themes.
    <section
      className={cx(
        "action-spotlight shrink-0 overflow-hidden rounded-xl lg:mx-3 lg:mt-3",
        className,
      )}
    >
      <div className="flex items-start gap-2.5 border-b border-line px-4 py-3">
        <span
          aria-hidden="true"
          className={`mt-1.5 size-1.5 shrink-0 rounded-full ${
            content?.accent ? "animate-pulse bg-money-600" : "bg-ink-subtle"
          }`}
        />
        <div className="min-w-0 space-y-1.5">
          <h2 className="text-sm font-semibold tracking-tight text-ink">{title}</h2>
          {content?.hint ? (
            <p className="text-xs leading-relaxed text-ink-muted">{content.hint}</p>
          ) : null}
          {/* The clock belongs with the action it is pressuring, not in a panel
              further down the sidebar. */}
          {timer && deadline ? (
            <CountdownPill
              deadline={deadline}
              initialLabel={timer.initialLabel}
              action={timer.label}
              tone="amber"
            />
          ) : null}
        </div>
      </div>
      <div className="space-y-3 px-4 py-3.5">
        {content?.body ?? (
          <p className="text-sm leading-relaxed text-ink-muted">{timer?.consequence}</p>
        )}
      </div>
    </section>
  );
}

type ActionContent = {
  title: string;
  hint?: string;
  accent: boolean;
  body: React.ReactNode;
};

function resolveActionContent(input: {
  room: CollabRoom;
  status: CollabStatus;
  isCreator: boolean;
  verified: boolean;
  canAct: boolean;
  canAccept: boolean;
  needsPayoutDetails: boolean;
  paymentDue: boolean;
  paymentOutstanding: boolean;
  dueMilestone: CollabRoom["milestones"][number] | null;
  paidMilestone: CollabRoom["milestones"][number] | null;
  paidProof: Proof | null;
  payMethods: PayMethodView[];
}): ActionContent | null {
  const {
    room,
    status,
    isCreator,
    verified,
    canAct,
    canAccept,
    needsPayoutDetails,
    paymentDue,
    paymentOutstanding,
    dueMilestone,
    paidMilestone,
    paidProof,
    payMethods,
  } = input;

  // Money actions beat workflow actions — someone is always waiting on cash.
  if (canAct && isCreator && paidMilestone) {
    return {
      title: "Confirm payment",
      hint: `${formatINR(paidMilestone.amount)} is marked as sent.`,
      accent: true,
      body: (
        <ConfirmReceipt
          collabId={room.collab.id}
          milestoneId={paidMilestone.id}
          proof={paidProof}
        />
      ),
    };
  }

  if (canAct && !isCreator && dueMilestone) {
    return {
      title: "Send payment",
      hint: `${formatINR(dueMilestone.amount)} is due now.`,
      accent: true,
      body: verified ? (
        room.payoutDetails ? (
          <PayNowDialog
            collabId={room.collab.id}
            milestoneId={dueMilestone.id}
            amountPaise={dueMilestone.amount}
            creatorName={room.creator.displayName}
            triggerLabel={`Pay ${formatINR(dueMilestone.amount)}`}
            methods={payMethods}
          />
        ) : (
          <p className="text-sm text-ink-muted">
            {room.creator.displayName} has not added payment details yet. Ask them to add a method
            before you send anything.
          </p>
        )
      ) : (
        <ConfirmSignIn collabId={room.collab.id} action="record a payment" />
      ),
    };
  }

  if ((status === "offer_pending" || status === "negotiating") && canAccept) {
    return {
      title: "Your move",
      accent: true,
      body: (
        <>
          {needsPayoutDetails ? (
            <Callout tone="amber" compact title="Add payment details first">
              They need somewhere to send the money.{" "}
              <Link
                href="/settings/payments"
                className="font-medium text-brand-700 hover:underline"
              >
                Add a UPI ID or bank account
              </Link>
              , then accept.
            </Callout>
          ) : null}

          <ActionForm action={acceptOfferAction}>
            <input type="hidden" name="collabId" value={room.collab.id} />
            <SubmitButton size="sm" full pendingLabel="Accepting…" disabled={needsPayoutDetails}>
              Accept terms
            </SubmitButton>
          </ActionForm>

          {!isCreator && !verified ? (
            <p className="text-center text-xs text-ink-muted">
              Sign in with WhatsApp before recording payments.
            </p>
          ) : null}

          <CounterOfferDialog room={room} />
          <CancelDisclosure
            collabId={room.collab.id}
            label="Withdraw from this collab"
            verified={verified}
          />
        </>
      ),
    };
  }

  if ((status === "in_progress" || status === "revision_requested") && isCreator && canAct) {
    return {
      title: status === "revision_requested" ? "Submit the revised draft" : "Submit your draft",
      accent: true,
      body: (
        <ActionForm action={submitDraftAction}>
          <input type="hidden" name="collabId" value={room.collab.id} />
          <Field
            label="Links to the draft"
            hint="Google Drive, Dropbox or any shareable link. One per line."
          >
            <textarea
              name="assetUrls"
              rows={2}
              required
              placeholder="https://drive.google.com/…"
              className="field-input resize-y"
            />
          </Field>
          <OptionalField label="Note for them" addLabel="Add a note for them">
            <textarea name="note" rows={2} className="field-input resize-y" />
          </OptionalField>
          <SubmitButton size="lg" full pendingLabel="Submitting…">
            Submit draft
          </SubmitButton>
        </ActionForm>
      ),
    };
  }

  if (status === "draft_submitted" && !isCreator && canAct) {
    return {
      title: "Review the draft",
      accent: true,
      body: (
        <>
          <DraftLinks room={room} />
          {verified ? (
            <>
              <ActionForm action={approveDraftAction}>
                <input type="hidden" name="collabId" value={room.collab.id} />
                <SubmitButton size="lg" variant="money" full pendingLabel="Approving…">
                  Approve draft
                </SubmitButton>
              </ActionForm>

              <Disclosure label="Request a revision instead" icon={<ReviseIcon />}>
                <ActionForm action={requestRevisionAction}>
                  <input type="hidden" name="collabId" value={room.collab.id} />
                  <Field label="What needs to change?">
                    <textarea name="note" rows={3} required className="field-input resize-y" />
                  </Field>
                  <SubmitButton variant="secondary" full pendingLabel="Sending…">
                    Request revision
                  </SubmitButton>
                </ActionForm>
              </Disclosure>
            </>
          ) : (
            <ConfirmSignIn collabId={room.collab.id} action="approve or send back this draft" />
          )}
        </>
      ),
    };
  }

  if (status === "draft_approved" && isCreator && canAct && !paymentOutstanding) {
    return {
      title: "Publish and share the link",
      accent: true,
      body: (
        <ActionForm action={submitLiveUrlAction}>
          <input type="hidden" name="collabId" value={room.collab.id} />
          <Field label="Link to the live post">
            <input
              name="publishedUrl"
              required
              placeholder="https://instagram.com/reel/…"
              className="field-input"
            />
          </Field>
          <SubmitButton size="lg" full pendingLabel="Submitting…">
            Submit live URL
          </SubmitButton>
        </ActionForm>
      ),
    };
  }

  if (status === "published" && !isCreator && canAct) {
    return {
      title: "Confirm the post is live",
      accent: true,
      body: (
        <>
          {room.latestPublished?.publishedUrl ? (
            <a
              href={room.latestPublished.publishedUrl}
              target="_blank"
              rel="noreferrer"
              className="block truncate rounded-xl border border-line bg-canvas px-3.5 py-2.5 text-sm text-brand-700 hover:bg-brand-50"
            >
              {room.latestPublished.publishedUrl}
            </a>
          ) : null}
          {verified ? (
            <ActionForm action={confirmLiveAction}>
              <input type="hidden" name="collabId" value={room.collab.id} />
              <SubmitButton size="lg" variant="money" full pendingLabel="Confirming…">
                Confirm it is live
              </SubmitButton>
            </ActionForm>
          ) : (
            <ConfirmSignIn collabId={room.collab.id} action="confirm the post is live" />
          )}
        </>
      ),
    };
  }

  // Negotiating but waiting on the other side — still one card, no form.
  if ((status === "offer_pending" || status === "negotiating") && canAct) {
    return {
      title: "Waiting on them",
      accent: false,
      body: (
        <>
          <p className="text-sm text-ink-muted">
            With the {isCreator ? "collaborator" : "creator"} — you can revise your counter anytime.
          </p>
          <CounterOfferDialog room={room} />
          <CancelDisclosure
            collabId={room.collab.id}
            label="Withdraw from this collab"
            verified={verified}
          />
        </>
      ),
    };
  }

  // Informational cards when nothing actionable for this side.
  const waiting = waitingCopy({
    status,
    isCreator,
    paymentDue,
    paymentOutstanding,
    paidMilestone,
    paidProof,
    room,
  });

  if (!waiting) return null;

  return {
    title: waiting.title,
    accent: false,
    body: waiting.body,
  };
}

function waitingCopy(input: {
  status: CollabStatus;
  isCreator: boolean;
  paymentDue: boolean;
  paymentOutstanding: boolean;
  paidMilestone: CollabRoom["milestones"][number] | null;
  paidProof: Proof | null;
  room: CollabRoom;
}): { title: string; body: React.ReactNode } | null {
  const { status, isCreator, paymentDue, paymentOutstanding, paidMilestone, paidProof, room } =
    input;

  if (status === "awaiting_payment") {
    return {
      title: isCreator ? "Waiting for the advance" : "Send the advance to start",
      body: (
        <p className="text-sm text-ink-muted">
          {isCreator
            ? "Terms are frozen. Once you confirm the advance has landed, the clock starts on your draft."
            : "Send the advance using the payment details below, then record it in the action box when it appears."}
        </p>
      ),
    };
  }

  if (status === "in_progress" && !isCreator) {
    return {
      title: "Creator is working",
      body: (
        <p className="text-sm text-ink-muted">
          You will get the draft to review before anything goes live.
        </p>
      ),
    };
  }

  if (status === "revision_requested" && !isCreator) {
    return {
      title: "Waiting for revision",
      body: (
        <p className="text-sm text-ink-muted">
          The creator is working on your feedback. You will be notified when the revised draft is
          ready.
        </p>
      ),
    };
  }

  if (status === "draft_submitted" && isCreator) {
    return {
      title: "With the collaborator",
      body: (
        <p className="text-sm text-ink-muted">
          They are reviewing. If they do not respond in time it approves automatically and the next
          payment falls due.
        </p>
      ),
    };
  }

  if (status === "draft_approved" && isCreator && paymentOutstanding) {
    return {
      title: paymentDue ? "Waiting for payment" : "Confirm payment first",
      body: (
        <p className="text-sm text-ink-muted">
          {paymentDue
            ? "They need to pay the due tranche first. Once you confirm it landed, you can publish."
            : paidMilestone
              ? "They say they have paid. Confirm it in the action box when you are ready."
              : "Waiting on payment before you can publish."}
        </p>
      ),
    };
  }

  if (status === "draft_approved" && !isCreator) {
    return {
      title: paymentOutstanding ? "Payment due" : "Approved",
      body: (
        <p className="text-sm text-ink-muted">
          {paymentDue
            ? "Pay the due tranche in the action box above when you are ready. The creator publishes after confirming."
            : paymentOutstanding
              ? "Waiting on the creator to confirm your payment."
              : "The draft is signed off. The creator will post and share the live link."}
        </p>
      ),
    };
  }

  if (status === "published" && isCreator) {
    return {
      title: "Awaiting confirmation",
      body: (
        <p className="text-sm text-ink-muted">
          They have a few days to confirm the post is live. After that it confirms automatically.
        </p>
      ),
    };
  }

  if (status === "cancelled" || status === "expired") {
    return {
      title: status === "expired" ? "Expired" : "Cancelled",
      body: (
        <p className="text-sm text-ink-muted">
          {status === "expired"
            ? "This offer lapsed without a response. Either side can start a fresh one."
            : "This collaboration ended. Anything already paid is between the two of you to settle."}
        </p>
      ),
    };
  }

  if (!isCreator && paidMilestone) {
    return {
      title: "Payment recorded",
      body: (
        <div className="space-y-2">
          <p className="text-sm text-ink-muted">
            Waiting on {room.creator.displayName} to confirm{" "}
            <Money paise={paidMilestone.amount} size="sm" className="text-ink" /> landed.
          </p>
          {paidProof ? <ProofRow collabId={room.collab.id} proof={paidProof} /> : null}
        </div>
      ),
    };
  }

  return null;
}

function ConfirmReceipt({
  collabId,
  milestoneId,
  proof,
}: {
  collabId: string;
  milestoneId: string;
  proof: Proof | null;
}) {
  return (
    <div className="space-y-2">
      {proof ? <ProofRow collabId={collabId} proof={proof} /> : null}

      <ActionForm action={confirmPaymentAction}>
        <input type="hidden" name="collabId" value={collabId} />
        <input type="hidden" name="milestoneId" value={milestoneId} />
        <SubmitButton size="lg" variant="money" full pendingLabel="Confirming…">
          Confirm the money arrived
        </SubmitButton>
      </ActionForm>

      <Disclosure label="It has not arrived" icon={<AlertIcon />} tone="danger">
        <ActionForm action={rejectPaymentAction}>
          <input type="hidden" name="collabId" value={collabId} />
          <input type="hidden" name="milestoneId" value={milestoneId} />
          <Field label="What is wrong?" hint="They can send it again with a clearer note.">
            <textarea
              name="reason"
              rows={2}
              required
              placeholder="Nothing has shown up in my account."
              className="field-input resize-y"
            />
          </Field>
          <SubmitButton variant="danger" full pendingLabel="Sending…">
            Flag as not received
          </SubmitButton>
        </ActionForm>
      </Disclosure>
    </div>
  );
}

function DraftLinks({ room }: { room: CollabRoom }) {
  const urls = (room.latestDraft?.assetUrls ?? []) as string[];
  if (urls.length === 0) return null;

  return (
    <ul className="space-y-1.5">
      {urls.map((url) => (
        <li key={url}>
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="block truncate rounded-xl border border-line bg-canvas px-3.5 py-2.5 text-sm text-brand-700 hover:bg-brand-50"
          >
            {url}
          </a>
        </li>
      ))}
      {room.latestDraft?.note ? (
        <li className="px-1 pt-1 text-sm text-ink-muted">{room.latestDraft.note}</li>
      ) : null}
    </ul>
  );
}

function ConfirmSignIn({ collabId, action }: { collabId: string; action: string }) {
  return (
    <div className="rounded-xl border border-line bg-canvas p-4">
      <p className="text-sm text-ink-muted">Sign in with WhatsApp to {action}.</p>
      <div className="mt-3">
        <LinkButton href={`/login?next=/collabs/${collabId}`} full>
          Sign in with WhatsApp
        </LinkButton>
      </div>
    </div>
  );
}

function CancelDisclosure({
  collabId,
  label,
  verified,
}: {
  collabId: string;
  label: string;
  verified: boolean;
}) {
  return (
    <div className="border-t border-line pt-2">
      <Disclosure label={label} icon={<WithdrawIcon />} tone="danger">
        {!verified ? (
          <ConfirmSignIn collabId={collabId} action="end this collab" />
        ) : (
          <ActionForm action={cancelCollabAction}>
            <input type="hidden" name="collabId" value={collabId} />
            <Field
              label="Reason"
              optional
              hint="Anything still scheduled stops being due. Money already paid is for the two of you to settle."
            >
              <textarea
                name="reason"
                rows={2}
                placeholder="Optional — share why if you want"
                className="field-input resize-y"
              />
            </Field>
            <SubmitButton variant="danger" full pendingLabel="Cancelling…">
              Confirm cancellation
            </SubmitButton>
          </ActionForm>
        )}
      </Disclosure>
    </div>
  );
}

function AlertIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" className="size-4" aria-hidden="true">
      <circle cx="8" cy="8" r="6.25" stroke="currentColor" strokeWidth="1.5" />
      <path d="M8 5v3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="8" cy="11" r="0.75" fill="currentColor" />
    </svg>
  );
}

function ReviseIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" className="size-4" aria-hidden="true">
      <path
        d="M6.5 4.5 4 7l2.5 2.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M4 7h5a3 3 0 0 1 0 6H7"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function WithdrawIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" className="size-4" aria-hidden="true">
      <circle cx="8" cy="8" r="6.25" stroke="currentColor" strokeWidth="1.5" />
      <path d="M5.75 8h4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}
